import { ContextualInfoButtonWithText } from "@/components/buttons/ContextualInfoButton";
import { CopyText } from "@/components/display/CopyText";
import { DescriptionList } from "@/components/display/DescriptionList";
import { SignificantHazards } from "@/components/status/SignificantHazards";
import { getRiskBandsMode, getTheme } from "@/features/branding";
import { GeocodingHelpButton } from "@/features/geocoders";
import type { ReturnPeriod } from "@/features/pe_aldd";
import { RiskBandsList } from "@/features/risk_bands";
import { anaHazards } from "@/lib/crg_apis/constants/hazards";
import ScreeningHazardExposures from "@/tools/risk-bands/components/ScreeningHazardExposures";
import { limitLatLngTo6dp, postProcessGeocodeQuality } from "@/utils/geocoding";
import CloseOutlined from "@ant-design/icons/es/icons/CloseOutlined";
import { ArrowRightIcon, WarningIcon } from "@phosphor-icons/react";
import { Button } from "antd";
import { useTranslation } from "react-i18next";
import type { AnalysisResultsTab, MapDisplayPoint, PointPopupMode, RiskiestSettings } from "../types";
export const PointDetailsPopup = ({
  activeTab = "risk",
  riskiestSettings,
  isRiskyPoint,
  point,
  displayYear,
  displayReturnPeriod,
  hideTitle = false,
  limitYears,
  limitReturnPeriods,
  popupMode = "full",
  onViewDetails,
  onClose,
  viewDetailsDisabled
}: {
  activeTab?: AnalysisResultsTab;
  fullWidth?: boolean;
  popupWidth?: number;
  riskiestSettings: RiskiestSettings | undefined;
  isRiskyPoint: boolean;
  point: MapDisplayPoint;
  displayYear?: number;
  displayReturnPeriod?: ReturnPeriod;
  limitYears: number[];
  limitReturnPeriods: ReturnPeriod[];
  riskBandsMode: ReturnType<typeof getRiskBandsMode>;
  popupMode?: PointPopupMode;
  hideTitle?: boolean;
  onViewDetails?: (point: MapDisplayPoint) => void;
  onClose?: () => void;
  viewDetailsDisabled?: boolean;
}) => {
  const theme = getTheme();
  const fontFamily = theme?.antd?.token?.fontFamily ?? "";
  const {
    t
  } = useTranslation("terms");
  if (popupMode === "simple") {
    return <PointInfo point={point} riskiestSettings={riskiestSettings} isRiskyPoint={isRiskyPoint} onViewDetails={onViewDetails} viewDetailsDisabled={viewDetailsDisabled} onClose={onClose} />;
  }
  if (activeTab === "exposure" && point.response.exposures) {
    return <div>
        {displayReturnPeriod ? <ScreeningHazardExposures limitReturnPeriods={limitReturnPeriods} exposure={point.response.exposures} displayReturnPeriod={displayReturnPeriod} hideTitle={hideTitle} /> : <div className="animate-fade-in-slide p-4">
            <div className="flex items-center justify-center gap-1 pt-4 text-base text-gray-500">
              <span>Please select a return period</span>
            </div>
          </div>}
      </div>;
  }
  return <div className="flex w-full flex-col gap-y-4" style={{
    fontFamily
  }} data-sentry-component="PointDetailsPopup" data-sentry-source-file="PointDetailsPopup.tsx">
      {/* // Show in Quick Batch. Only points in a portfolio have names */}
      {!point.name && <PointInfo onClose={onClose} point={point} riskiestSettings={riskiestSettings} isRiskyPoint={isRiskyPoint} />}

      <div className="w-full space-y-3">
        <ContextualInfoButtonWithText id="simpleRiskRatings" data-sentry-element="ContextualInfoButtonWithText" data-sentry-source-file="PointDetailsPopup.tsx">
          <h4 className="m-0 text-base !font-semibold">{t("simpleRiskRatings")}</h4>
        </ContextualInfoButtonWithText>
        <RiskBandsList labelFontSize="14px" limitYears={limitYears} ratings={point.response} size={56} data-sentry-element="RiskBandsList" data-sentry-source-file="PointDetailsPopup.tsx" />
      </div>

      {"top_significant_hazards" in point.response && point.metadata && <div className="w-full pt-2">
          <SignificantHazards significantHz={point.response.top_significant_hazards} incorporatedHz={point.metadata.incorporated_hazards} year={displayYear} />
        </div>}
    </div>;
};
const PointInfo = ({
  point,
  riskiestSettings,
  isRiskyPoint,
  onViewDetails,
  onClose,
  viewDetailsDisabled
}: {
  point: MapDisplayPoint;
  riskiestSettings?: RiskiestSettings;
  isRiskyPoint: boolean;
  onViewDetails?: (point: MapDisplayPoint) => void;
  onClose?: () => void;
  viewDetailsDisabled?: boolean;
}) => {
  const {
    t
  } = useTranslation("terms");
  const latLng = limitLatLngTo6dp(point.latLng);
  const items = [...(point.displayInput ? [{
    label: t("userSuppliedId"),
    value: <CopyText value={point.displayInput} className="text-sm" />
  }] : []), {
    label: t("coordinates"),
    value: <CopyText value={`${latLng.lat}, ${latLng.lng}`} className="text-sm" />
  }, ...(point.hk ? [{
    label: "TPU / Subunit",
    value: `${point.hk?.tpu || "No Data"} / ${point.hk?.subunit || "No Data"}`
  }] : []), {
    label: <span className="flex items-center gap-1">
          {t("geocodeQuality")}
          <GeocodingHelpButton />
        </span>,
    value: postProcessGeocodeQuality({
      quality: point?.quality,
      latitude: point.latLng.lat,
      longitude: point.latLng.lng
    })
  }];
  const pointName = point.displayInput || point.input || "Unnamed asset";
  return <div className="flex flex-col gap-2" data-sentry-component="PointInfo" data-sentry-source-file="PointDetailsPopup.tsx">
      <div className="item-center flex justify-between">
        <CopyText value={pointName} className="w-full text-base font-semibold tracking-[-0.01em]" data-sentry-element="CopyText" data-sentry-source-file="PointDetailsPopup.tsx" />
        <Button type="text" onClick={onClose} icon={<CloseOutlined />} data-sentry-element="Button" data-sentry-source-file="PointDetailsPopup.tsx" />
      </div>

      <DescriptionList items={items} data-sentry-element="DescriptionList" data-sentry-source-file="PointDetailsPopup.tsx" />
      <RiskiestPoint riskiestSettings={riskiestSettings} isRiskyPoint={isRiskyPoint} data-sentry-element="RiskiestPoint" data-sentry-source-file="PointDetailsPopup.tsx" />
      {onViewDetails && <Button type="primary" size="small" block className="mt-1" disabled={viewDetailsDisabled} loading={viewDetailsDisabled} onClick={() => onViewDetails(point)} icon={<ArrowRightIcon />} iconPlacement="end">
          View Point-Level Results
        </Button>}
    </div>;
};
const RiskiestPoint = ({
  riskiestSettings,
  isRiskyPoint
}: {
  riskiestSettings?: RiskiestSettings;
  isRiskyPoint: boolean;
}) => {
  const theme = getTheme();
  const fontFamily = theme?.antd?.token?.fontFamily ?? "";
  const {
    t
  } = useTranslation("terms");
  if (!riskiestSettings || !isRiskyPoint) {
    return null;
  }
  return <div className="grid grid-cols-[18px_1fr] items-center justify-start gap-1 rounded-lg border border-amber-200 bg-amber-50 p-2 text-sm" style={{
    fontFamily
  }} data-sentry-component="RiskiestPoint" data-sentry-source-file="PointDetailsPopup.tsx">
      <WarningIcon size={16} className="text-amber-700" weight="fill" data-sentry-element="WarningIcon" data-sentry-source-file="PointDetailsPopup.tsx" />
      <ContextualInfoButtonWithText id="riskiestPoint" data-sentry-element="ContextualInfoButtonWithText" data-sentry-source-file="PointDetailsPopup.tsx">
        <div className="font-semibold text-amber-700">Riskiest Point</div>
      </ContextualInfoButtonWithText>
      <div className="col-start-2 text-left text-amber-700">
        Projected to have highest{" "}
        {riskiestSettings.hazard === "total" ? "total" : anaHazards[riskiestSettings.hazard].name.toLowerCase()} risk in{" "}
        {riskiestSettings.year} (under {t("scenarios.rcp85.short")} scenario)
      </div>
    </div>;
};